import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import BedbankSummaryPriceDetails from './BedbankSummaryPriceDetails'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import BedbankCancellationPolicy from './BedbankCancellationPolicy'
import WalledContent from 'components/Common/WalledContent'
import { useBedbankSelectDatesWithFlights } from 'hooks/OfferPage/useBedbankSelectDates'
import BedbankPromotionCancellationPolicy from 'components/OfferPage/Bedbank/BedbankPromotionCancellationPolicy'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import BedbankOfferInclusions from './BedbankOfferInclusions'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'

export const DetailSegment = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  position: relative;
  padding: ${rem(20)} ${rem(24)};
  display: flex;
  flex-direction: column;
  gap: ${rem(16)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    min-height: ${rem(160)};
  }
`

const LeftDetailSegment = styled.div`
  > * + * {
    margin-top: ${rem(16)}
  }
  
  ${mediaQueryUp.desktop} {
    flex: 1 0 0;
    align-self: flex-start;
  }
`

const RightDetailSegment = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: ${rem(16)};
  position: relative;
  width: auto;
  min-width: ${rem(180)};
  
  ${mediaQueryUp.desktop} {
    align-self: flex-end;
    flex: 0 0 40%;
  }
`
const TravelPeriodSection = styled.div`
  display: flex;
  flex-direction: column;
`

const BuyButton = styled(TextButton)`
  margin-top: ${rem(16)};
`

interface Props {
  offer: App.BedbankOffer;
  packageView: App.BedbankOfferPackageView
  rooms: Array<App.Occupants>;
  duration: number;
  showNonCancellation?: boolean;
  checkInDate?: string;
  checkOutDate?: string;
  flightsEnabled: boolean;
  flightPrice?: number;
}

function BedbankSummaryPromotion({ offer, packageView, checkInDate, checkOutDate, showNonCancellation, rooms, duration, flightsEnabled, flightPrice }: Props) {
  const hasDates = !!(checkInDate && checkOutDate)

  const pkg = packageView.package
  const rate = packageView.cheapestRate
  let bundleRate
  if (rate) {
    bundleRate = packageView.bundleRates.find((bundleRate) => bundleRate.id === rate.id)
  }

  const promotion = packageView.promotions[rate?.promotionId ?? '']

  const openDateSelector = useBedbankSelectDatesWithFlights(pkg.roomId)

  const handleBuyButtonClick = () => {
    openDateSelector()
    Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomId, packageOptionPrice: rate?.totals?.total }))
  }

  if (!promotion) {
    return null
  }

  return (
    <DetailSegment>
      <LeftDetailSegment>
        <ProductTypeLabel productType="limited_time_special" />
        <Heading variant="heading5">{promotion.name}</Heading>
        {rate && (
          <BedbankCancellationPolicy rate={rate} offer={offer} showNonCancellation={!!showNonCancellation} />
        )}
        {!hasDates && promotion.cancellationPolicy && (
          <BedbankPromotionCancellationPolicy policyCode={promotion.cancellationPolicy} />
        )}

        <BedbankOfferInclusions
          heading="Special inclusions for select packages only"
          content={promotion.rateInclusionsLong}
          inclusions={promotion.inclusions}
          maxHeight={160}
        />
        <TravelPeriodSection>
          <BodyText variant="small" weight="normal" colour="neutral-two">
            Travel between: {moment(promotion.travelPeriod.from).format(DMY_CASUAL_SHORT_FORMAT)} - {moment(promotion.travelPeriod.to).format(DMY_CASUAL_SHORT_FORMAT)}.
            Minimum stay: {pluralizeToString('night', promotion.los)}
          </BodyText>
        </TravelPeriodSection>
      </LeftDetailSegment>
      <RightDetailSegment>
        {offer.sell && (
          <WalledContent enforced={offer.walledGarden} hidePricing={offer.hidePricing}>
            <BedbankSummaryPriceDetails
              rate={rate}
              bundleRate={bundleRate}
              rooms={rooms}
              offer={offer}
              duration={duration}
              flightPrice={flightPrice}
              flightsEnabled={flightsEnabled && offer.promoteAsBundle}
              promotion
              alignment="start"
            />
          </WalledContent>
        )}

        <BuyButton
          kind="primary"
          data-filter="checkin"
          fit="full-width"
          onClick={handleBuyButtonClick}
        >
          Check availability
        </BuyButton>
      </RightDetailSegment>
    </DetailSegment>
  )
}

export default BedbankSummaryPromotion
